import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2, Facebook, Instagram, Clock, BadgePercent } from "lucide-react";
import { SITE, SERVICES } from "@/lib/site";
import { clarityEvent } from "@/lib/clarity";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Fyll i namn, e-post och meddelande.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Tack! Vi återkommer så snart som möjligt.");
      clarityEvent("contact_form_submit");
      setForm(empty);
    } catch (err) {
      toast.error("Något gick fel. Försök igen eller ring oss direkt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="py-20 md:py-28 text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-[#0B132B]/45 backdrop-blur-sm border-t-2 border-white/10" />
      <div className="absolute -bottom-32 -left-20 w-[28rem] h-[28rem] bg-[#D92525] opacity-15 blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 md:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: form */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 font-display font-bold text-xs uppercase tracking-[0.18em] text-[#D92525]">
              <span className="w-8 h-[2px] bg-[#D92525]" /> 04 — Kontakt
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter mt-3 leading-[1]">
              Få en <span className="text-[#D92525]">fri offert</span>
              <br />
              redan idag.
            </h2>
            <p className="font-body text-white/75 mt-5 max-w-xl">
              Fyll i formuläret så kontaktar vi dig snart. Vi kommer gärna hem till
              dig för en kostnadsfri besiktning.
            </p>

            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white p-6 md:p-8"
            >
              <div className="sm:col-span-1">
                <label className="block font-display font-bold text-xs uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                  Namn *
                </label>
                <input
                  data-testid="contact-input-name"
                  className="swed-input"
                  type="text"
                  placeholder="För- och efternamn"
                  value={form.name}
                  onChange={update("name")}
                  required
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block font-display font-bold text-xs uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                  E-post *
                </label>
                <input
                  data-testid="contact-input-email"
                  className="swed-input"
                  type="email"
                  placeholder="din@email.se"
                  value={form.email}
                  onChange={update("email")}
                  required
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block font-display font-bold text-xs uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                  Telefon
                </label>
                <input
                  data-testid="contact-input-phone"
                  className="swed-input"
                  type="tel"
                  placeholder="07X-XXX XX XX"
                  value={form.phone}
                  onChange={update("phone")}
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block font-display font-bold text-xs uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                  Tjänst
                </label>
                <select
                  data-testid="contact-input-service"
                  className="swed-input"
                  value={form.service}
                  onChange={update("service")}
                >
                  <option value="">Välj tjänst</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title}>
                      {s.title}
                    </option>
                  ))}
                  <option value="Annat">Annat</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block font-display font-bold text-xs uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                  Meddelande *
                </label>
                <textarea
                  data-testid="contact-input-message"
                  className="swed-input"
                  rows={5}
                  placeholder="Berätta om ditt projekt – adress, storlek, önskemål..."
                  value={form.message}
                  onChange={update("message")}
                  required
                />
              </div>
              <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4">
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="btn-primary disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Skickar...
                    </>
                  ) : (
                    <>
                      Skicka förfrågan
                      <Send size={16} className="ml-2" />
                    </>
                  )}
                </button>
                <p className="font-body text-xs text-[#4B5563]">
                  Vi svarar oftast inom 24 timmar.
                </p>
              </div>
            </form>
          </div>

          {/* Right: contact info */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <a
              href={SITE.phoneHref}
              data-testid="contact-info-phone"
              className="border-2 border-white p-7 hover:bg-[#D92525] hover:border-[#D92525] transition-colors group"
            >
              <Phone size={26} className="text-[#D92525] group-hover:text-white" />
              <div className="font-display text-xs uppercase tracking-[0.18em] text-white/60 group-hover:text-white/90 mt-4">
                Ring oss direkt
              </div>
              <div className="font-display text-3xl md:text-4xl font-black mt-1 tracking-tight">
                {SITE.phone}
              </div>
            </a>

            <div
              data-testid="contact-info-social"
              className="border-2 border-white p-7 bg-white/[0.03]"
            >
              <div className="flex items-center gap-2">
                <Facebook size={22} className="text-[#D92525]" />
                <Instagram size={22} className="text-[#D92525]" />
              </div>
              <div className="font-display text-xs uppercase tracking-[0.18em] text-white/60 mt-4">
                Följ oss i sociala medier
              </div>
              <div className="font-display text-xl md:text-2xl font-black mt-1 tracking-tight leading-tight">
                Följ våra senaste projekt
              </div>
              <p className="font-body text-white/70 mt-2 text-sm leading-relaxed">
                Se bilder från pågående fasadrenoveringar och hitta inspiration för
                ditt eget hem.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href={SITE.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-facebook-link"
                  className="inline-flex items-center gap-2 border-2 border-white px-4 py-2 font-display font-bold text-xs tracking-[0.15em] uppercase hover:bg-white hover:text-[#0B132B] transition-colors"
                >
                  <Facebook size={14} /> Facebook
                </a>
                <a
                  href={SITE.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-link"
                  className="inline-flex items-center gap-2 border-2 border-white px-4 py-2 font-display font-bold text-xs tracking-[0.15em] uppercase hover:bg-white hover:text-[#0B132B] transition-colors"
                >
                  <Instagram size={14} /> Instagram
                </a>
              </div>
            </div>

            <a
              href={`mailto:${SITE.email}`}
              data-testid="contact-info-email"
              className="border-2 border-white p-7 hover:bg-white hover:text-[#0B132B] transition-colors group"
            >
              <Mail size={26} className="text-[#D92525]" />
              <div className="font-display text-xs uppercase tracking-[0.18em] text-white/60 group-hover:text-[#0B132B]/70 mt-4">
                E-post
              </div>
              <div className="font-display text-2xl md:text-3xl font-black mt-1 tracking-tight break-all">
                {SITE.email}
              </div>
            </a>

            <div
              data-testid="contact-info-hours"
              className="border-2 border-white p-7"
            >
              <Clock size={26} className="text-[#D92525]" />
              <div className="font-display text-xs uppercase tracking-[0.18em] text-white/60 mt-4">
                Öppettider
              </div>
              <ul className="font-body text-sm mt-3 divide-y divide-white/10">
                <li className="flex items-center justify-between py-2">
                  <span className="text-white/85">Måndag – Fredag</span>
                  <span className="font-display font-black tracking-tight text-white">
                    07:00 – 16:00
                  </span>
                </li>
                <li className="flex items-center justify-between py-2">
                  <span className="text-white/85">Lördag – Söndag</span>
                  <span className="font-display font-black tracking-tight text-[#FFB3B3]">
                    Stängt
                  </span>
                </li>
              </ul>
            </div>

            <div
              data-testid="contact-info-rot"
              className="border-2 border-white p-7 bg-white/[0.03]"
            >
              <BadgePercent size={26} className="text-[#D92525]" />
              <div className="font-display text-xs uppercase tracking-[0.18em] text-white/60 mt-4">
                ROT-avdrag
              </div>
              <p className="font-body text-white/85 mt-3 text-sm leading-relaxed">
                Som privatkund kan du ha rätt till ROT-avdrag för godkända måleri- och
                renoveringsarbeten. Vi gör avdraget direkt på fakturan när villkoren är
                uppfyllda och ansöker därefter hos Skatteverket.
              </p>
              <p className="font-body text-white/85 mt-3 text-sm leading-relaxed">
                ROT-avdrag gäller endast arbetskostnaden. Material, resor och andra kostnader
                omfattas inte.
              </p>
              <p className="font-body text-white/55 mt-3 text-xs leading-relaxed">
                Regler och villkor kan ändras. Aktuell information finns hos Skatteverket.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
